import { Box } from '@mui/material';

import { RelatedCountFields } from '~/components/common/RelatedCountFields';
import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { PropertyDetailPageHeader } from '~/components/portfolio/detailPage/PropertyDetailPageHeader';
import { PropertyTabContainer } from '~/components/portfolio/detailPage/PropertyTabContainer';
import { usePropertyRelatedCounts } from '~/components/portfolio/detailPage/usePropertyRelatedCounts';
import type { PropertyDetails } from '~/components/portfolio/types';
import { useNeedsAttentionCount } from '~/components/workQueue/useNeedsAttentionItems';
import { usePropertyKnowledge } from '~/data/hooks/useKnowledge';
import { DetailTableHeightProvider } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { pageBandColor } from '~/theme';

// The preview has no tab strip, so the counts its tabs would carry are listed instead.
function PropertyRelatedCounts({ propertyId }: { propertyId: string }) {
  const terms = useLocaleTerms();
  const counts = usePropertyRelatedCounts(propertyId);
  const needsAttention = useNeedsAttentionCount(propertyId);
  const knowledgeEnabled = useKnowledgeEnabled();
  const knowledge = usePropertyKnowledge(propertyId);
  const knowledgeCount =
    knowledge.isLoading || knowledge.error
      ? undefined
      : knowledge.entries.filter((e) => !e.supersededById).length;
  return (
    <RelatedCountFields
      counts={[
        { label: 'Needs Attention', value: needsAttention },
        ...(knowledgeEnabled ? [{ label: 'Knowledge', value: knowledgeCount }] : []),
        { label: terms.elevators, value: counts.elevators },
        { label: 'Tickets', value: counts.tickets },
        { label: 'Invoices', value: counts.invoices },
        { label: 'Proposals', value: counts.proposals },
      ]}
    />
  );
}

interface PropertyDetailPageProps {
  property: PropertyDetails;
  onBack: (() => void) | null;
  backLabel: string;
  variant?: 'page' | 'preview';
}

/**
 * Full-page property detail: a header band naming the building, carrying the record
 * around it and the period's performance figures, over five full-width tabs — what
 * needs attention, its elevators, the contracts over them, the tickets raised against
 * them and what was billed for them.
 *
 * The Overview tab is absorbed into the band as a KPI strip. The property comes from
 * the route loader, so unlike the other detail pages there is no loading state to hold
 * here.
 */
export function PropertyDetailPage({
  property,
  onBack,
  backLabel,
  variant = 'page',
}: PropertyDetailPageProps) {
  const isPreview = variant === 'preview';
  // Read here as well as in the tab strip so the header can name the elevator
  // count. Same SWR key both times, so it is one request.
  const { elevators } = usePropertyRelatedCounts(property.id);

  return (
    <DetailTableHeightProvider>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100%"
        bgcolor={isPreview ? pageBandColor : 'grey.50'}
      >
        <PropertyDetailPageHeader
          property={property}
          onBack={onBack}
          backLabel={backLabel}
          elevatorCount={elevators}
          compact={isPreview}
          footer={isPreview ? <PropertyRelatedCounts propertyId={property.id} /> : null}
        />

        {isPreview ? null : (
          <Box display="flex" flex={1} minHeight={0} minWidth={0}>
            <PropertyTabContainer property={property} />
          </Box>
        )}
      </Box>
    </DetailTableHeightProvider>
  );
}
